/**
 * RTK Query endpoints — Users
 *
 * Migrated from: src/pages/users/actions.js
 *
 * Pass the same `{ range, filters, sort }` params shape the legacy
 * `getUsers(params)` used so cache keys stay stable across the migration.
 *
 * Photo uploads use the shared `useUploadFileMutation` from uploadApi.js
 * (two-step signed-URL flow) — pages call that first, then pass the upload
 * key to `useEditUserMutation` / `useCreateUserMutation`.
 */

import { baseApi } from './baseApi';
import { buildQueryString } from './utils';

const userApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    userList: builder.query({
      query: (params = {}) => ({
        url: `/su/users${buildQueryString(params)}`,
        method: 'GET',
      }),
      providesTags: (result) =>
        result?.items
          ? [
              ...result.items.map(({ id }) => ({ type: 'User', id })),
              { type: 'User', id: 'LIST' },
            ]
          : [{ type: 'User', id: 'LIST' }],
    }),

    createUser: builder.mutation({
      query: (data) => ({
        url: '/su/users',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: 'User', id: 'LIST' }],
    }),

    editUser: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/su/users/${id}/edit`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'User', id },
        { type: 'User', id: 'LIST' },
      ],
    }),

    deleteUser: builder.mutation({
      query: (id) => ({
        url: `/su/users/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'User', id },
        { type: 'User', id: 'LIST' },
      ],
    }),

    changePassword: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/su/users/${id}/change-password`,
        method: 'PUT',
        body: data,
      }),
      // No invalidation — password change has no list-visible side effects.
    }),

    manageNotifications: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/su/users/${id}/manage-notifications`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'User', id }],
    }),
  }),
  overrideExisting: false,
});

export const {
  useUserListQuery,
  useCreateUserMutation,
  useEditUserMutation,
  useDeleteUserMutation,
  useChangePasswordMutation,
  useManageNotificationsMutation,
} = userApi;

export default userApi;
